import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Typography,
  Row,
  Col,
  DatePicker,
  Select,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import { apiClient } from "../Api/AllApi";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Title } = Typography;

const CategoryProductReport = () => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get("/categories");
        const categoriesData = response.data.map((category) => {
          const nameObj = JSON.parse(category.name || "{}");
          const displayName =
            nameObj[i18n.language] || nameObj.en || nameObj.ar || "Unknown";
          return {
            id: category.id,
            name: displayName,
          };
        });
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
        message.error(t("Failed to fetch categories"));
      }
    };

    fetchCategories();
  }, [i18n.language, t]);

  useEffect(() => {
    // Set default date range to the past 30 days
    const today = dayjs();
    const past30Days = today.subtract(30, "days");
    setDateRange([past30Days, today]);
  }, []);

  useEffect(() => {
    if (!dateRange || dateRange.length !== 2) {
      // Do not fetch data until the date range is set
      return;
    }

    const fetchReportData = async () => {
      try {
        const response = await apiClient.get("/reports/sold-by-category", {
          params: {
            category_id: selectedCategory,
            start_date: dateRange[0]?.format("YYYY-MM-DD"),
            end_date: dateRange[1]?.format("YYYY-MM-DD"),
          },
        });

        const processedData = response.data.map((category) => {
          let categoryName;
          try {
            const parsedNameC = JSON.parse(category.category_name || "{}");

            categoryName =
              parsedNameC[i18n.language] ||
              parsedNameC.en ||
              parsedNameC.ar ||
              category.category_name;
          } catch (error) {
            console.error(
              "Error parsing category name:",
              category.category_name,
              error
            );
            categoryName = category.category_name;
          }

          const products = category.products.map((product) => {
            let productName;
            try {
              const parsedName = JSON.parse(product.product_name || "{}");
              productName =
                parsedName[i18n.language] ||
                parsedName.en ||
                parsedName.ar ||
                product.product_name;
            } catch (error) {
              console.error(
                "Error parsing product name:",
                product.product_name,
                error
              );
              productName = product.product_name;
            }
            return {
              ...product,
              product_name: productName,
            };
          });

          // Calculate total quantity and weight for the category
          const totalQuantity = products.reduce(
            (sum, product) => sum + product.quantity_sold,
            0
          );
          const totalWeight = products.reduce(
            (sum, product) => sum + product.total_weight,
            0
          );

          return {
            category_name: categoryName,
            products,
            total_quantity: totalQuantity,
            total_weight: totalWeight,
          };
        });

        // Filter data if a specific category is selected
        const filteredData = selectedCategory
          ? processedData.filter(
              (category) =>
                category.category_name ===
                categories.find((cat) => cat.id === selectedCategory)?.name
            )
          : processedData;

        // Recalculate totals for filtered data
        const recalculatedData = filteredData.map((category) => {
          const totalQuantity = category.products.reduce(
            (sum, product) => sum + product.quantity_sold,
            0
          );
          const totalWeight = category.products.reduce(
            (sum, product) => sum + product.total_weight,
            0
          );
          return {
            ...category,
            total_quantity: totalQuantity,
            total_weight: totalWeight,
          };
        });

        const sortedReportData = recalculatedData.sort((a, b) => {
          if (a.category_name === "Uncategorized") return 1;
          if (b.category_name === "Uncategorized") return -1;
          return a.category_name.localeCompare(b.category_name);
        });

        setReportData(sortedReportData);
      } catch (error) {
        console.error("Error fetching report data:", error);
        message.error(t("Failed to fetch report data"));
      }
    };

    fetchReportData();
  }, [dateRange, selectedCategory, i18n.language]);

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>{t("Category Product Report")}</Title>
      <Card style={{ marginBottom: "20px" }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("Select a category")}
              onChange={(value) => setSelectedCategory(value)}
            >
              {categories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={(dates) => setDateRange(dates)}
              value={dateRange} // Ensure the date range is displayed in the UI
            />
          </Col>
        </Row>
      </Card>

      {reportData.map((category) => (
        <Card
          key={category.category_name}
          title={category.category_name}
          style={{ marginBottom: "20px" }}
          extra={
            <div>
              <span>
                {t("کۆی فرۆشی گونیە")}: {category.total_quantity}
              </span>
              <br />
              <span>
                {t("کۆی فرۆشی کێش")}: {category.total_weight} kg
              </span>
            </div>
          }
        >
          <Table
            columns={[
              {
                title: t("Product"),
                dataIndex: "product_name",
                key: "product_name",
              },
              {
                title: t("Quantity Sold"),
                dataIndex: "quantity_sold",
                key: "quantity_sold",
              },
              {
                title: t("Total Weight"),
                dataIndex: "total_weight",
                key: "total_weight",
              },
            ]}
            dataSource={category.products}
            rowKey={(record) => record.product_name}
            pagination={false}
          />
        </Card>
      ))}
    </div>
  );
};

export default CategoryProductReport;
